import React, { FC, useEffect, useRef, useState } from 'react'
import AppPageComponent from '@components/page/appPage.component'
import PageHeaderComponent from '@components/page/pageHeader.component'
import PageBodyComponent from '@components/page/pageBody.component'
import {
  Button,
  Card,
  Col,
  FormControl,
  InputGroup,
  ListGroup,
  Row,
} from 'react-bootstrap'
import { DjangoFilter, SortOrder } from '@store/slices/generic.slice'
import {
  clearAllNews,
  deleteNews,
  fetchAllNewss,
  NewsModel,
  NewsState,
  selectAllNews,
  selectNewsLoading,
} from '@store/slices/news.slice'
import { BsPlusCircle, BsPlusCircleFill } from 'react-icons/bs'
import {
  clearAllNotes,
  DailyNoteModel,
  fetchAllNotes,
  selectAllNotes,
} from '@store/slices/note.slice'
import NewsEditorComponent from '@components/news/newsEditor/newsEditor.component'
import { useTheme } from '@config/theme.config'

import ReactSelect from 'react-select'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import NoteCard from '@components/note/noteCard.component'
import { reactSelectDarkmode } from '@config/slate.config'
import NewsRow from '@components/news/newsRow'
import InfiniteScroll from 'react-infinite-scroll-component'
import { areDatesEqual, formatDate } from '../tools/time.tools'
import { getSelectButtonClass } from '../tools/style.tools'
import { getNewsStateTranslation } from '../tools/newsState.tools'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAllStations,
  selectAllStations,
  selectStationLoading,
} from '@store/slices/station.slice'
import {
  fetchAllAreas,
  selectAllAreas,
  selectAreaLoading,
} from '@store/slices/area.slice'
import {
  selectAllSections,
  selectSectionLoading,
} from '@store/slices/section.slice'
import {
  selectAllAuthors,
  selectAuthorsLoading,
} from '@store/slices/author.slice'
import { AppDispatch } from '@store/store'
import { fetchAllSections } from '@store/thunk/section.thunk'
import { fetchAllAuthors } from '@store/thunk/author.thunk'
import { toast } from 'react-toastify'

const NewsPage: React.FC = () => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const stations = useSelector(selectAllStations)
  const isStationsLoading = useSelector(selectStationLoading)
  const areas = useSelector(selectAllAreas)
  const isAreasLoading = useSelector(selectAreaLoading)
  const sections = useSelector(selectAllSections)
  const isSectionsLoading = useSelector(selectSectionLoading)
  const authors = useSelector(selectAllAuthors)
  const isAuthorsLoading = useSelector(selectAuthorsLoading)
  const newss = useSelector(selectAllNews)
  const isNewsLoading = useSelector(selectNewsLoading)
  const notes = useSelector(selectAllNotes)

  const [stationFilter, setStationFilter] = useState<EntityId | null>(null)
  const [areaFilter, setAreaFilter] = useState<EntityId | null>(null)
  const [sectionFilter, setSectionFilter] = useState<string | null>(null)
  const [authorFilter, setAuthorFilter] = useState<string | null>(null)
  const [sorting, setSorting] = useState<SortOrder>(SortOrder.DESCENDING)
  const [dateFrom, setDateFrom] = useState<string | null>(
    formatDate(new Date())
  )
  const [dateTo, setDateTo] = useState<string | null>(formatDate(new Date()))
  const [stateFilter, setStateFilter] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [editNews, setEditNews] = useState<NewsModel | undefined>(undefined)
  const [newNote, setNewNote] = useState<Partial<DailyNoteModel> | undefined>(
    undefined
  )
  const [newsRefreshed, setNewsRefreshed] = useState<boolean>(false)

  useEffect(() => {
    if (stations.length === 0 && !isStationsLoading)
      dispatch(fetchAllStations())
    if (areas.length === 0 && !isAreasLoading) dispatch(fetchAllAreas())
    if (sections.length === 0 && !isSectionsLoading)
      dispatch(fetchAllSections())
    if (authors.length === 0 && !isAuthorsLoading) dispatch(fetchAllAuthors())
    if (!isNewsLoading) {
      handleFetchParamsChange()
      fetchNews()
    }
    onAddNote()
  }, [])

  useEffect(() => {
    if (
      stations.length > 0 &&
      areas.length > 0 && // authors.length > 0 &&
      sections.length > 0
    ) {
      fetchNews()
    }
  }, [
    dispatch,
    page,
    search,
    stationFilter,
    areaFilter,
    sectionFilter,
    authorFilter,
    sorting,
    dateFrom,
    dateTo,
    stateFilter,
  ])

  useEffect(() => {
    if (dateTo && dateFrom && !isNewsLoading) fetchNotes()
  }, [dispatch, dateFrom, dateTo])

  const onEditNews = (news: NewsModel) => {
    setEditNews(news)
    setShowModal(true)
  }

  const onDeleteNews = (newsToRemove: NewsModel) => {
    dispatch(deleteNews(String(newsToRemove.id)))
      .then(unwrapResult)
      .then(() => toast.success('Zpráva smazána'))
      .catch((e) => console.error(e))
  }

  const fetchMoreData = () => {
    setPage((page) => page + 1)
  }

  const handleFetchParamsChange = () => {
    dispatch(clearAllNews({}))
    setPage(0)
  }

  const handleSearchChange = (searchValue: string) => {
    handleFetchParamsChange()
    // @ts-ignore
    setSearch(searchValue)
  }

  const handleStationFilterChange = (value: EntityId | null) => {
    handleFetchParamsChange()
    setAreaFilter(null)
    setStationFilter(value)
  }

  const handleAreaFilterChange = (value: EntityId | null) => {
    handleFetchParamsChange()
    setAreaFilter(value)
  }

  const handleSectionFilterChange = (value: string | null) => {
    handleFetchParamsChange()
    setSectionFilter(value)
  }

  const handleAuthorFilterChange = (value: string | null) => {
    handleFetchParamsChange()
    setAuthorFilter(value)
  }

  const handleStateFilterChange = (value: NewsState | null) => {
    handleFetchParamsChange()
    setStateFilter(value)
  }

  const handleSortFilterChange = (value: SortOrder) => {
    handleFetchParamsChange()
    setSorting(value)
  }

  const handleDateFromChange = (value: string) => {
    handleFetchParamsChange()
    setDateFrom(value)
  }

  const handleDateToChange = (value: string) => {
    handleFetchParamsChange()
    setDateTo(value)
  }

  const fetchNews = () => {
    const filter = new DjangoFilter<NewsModel>()

    // Set limit and offset for Pagination
    const itemsPerPage = 8
    filter.setLimit(itemsPerPage)
    filter.setOffset(itemsPerPage * page)

    // Set Sort Order
    filter.setSortOrder(sorting)

    // Add Sort Field(s)
    filter.addSortField('updated_at')

    filter.setSearch(search)

    // Add any other required filters
    filter.addFilter({
      news_state: stateFilter ? stateFilter : undefined,
      station_info__id: stationFilter ? stationFilter : undefined,
      area_info__id: areaFilter ? areaFilter : undefined,
      section__name: sectionFilter ? sectionFilter : undefined,
      created_by__username: authorFilter ? authorFilter : undefined,
    })

    if (dateFrom && !dateTo) {
      filter.addDateFilter('date', new Date(dateFrom), new Date(dateFrom))
    } else if (!dateFrom && dateTo) {
      filter.addDateFilter('date', new Date(dateTo), new Date(dateTo))
    } else if (dateFrom && dateTo) {
      filter.addDateFilter('date', new Date(dateFrom), new Date(dateTo))
    }

    // Dispatch the fetchAllNews method with the filter
    dispatch(fetchAllNewss(filter)).then(() => setNewsRefreshed(true))
  }

  const fetchNotes = () => {
    dispatch(clearAllNotes({}))
    const filter = new DjangoFilter<DailyNoteModel>()
    if (dateFrom && !dateTo) {
      filter.addDateFilter('date', new Date(dateFrom), new Date(dateFrom))
    } else if (!dateFrom && dateTo) {
      filter.addDateFilter('date', new Date(dateTo), new Date(dateTo))
    } else if (dateFrom && dateTo) {
      filter.addDateFilter('date', new Date(dateFrom), new Date(dateTo))
    }

    // Dispatch the fetchAllNews method with the filter
    dispatch(fetchAllNotes(filter))
  }

  const onAddNews = () => {
    setEditNews(undefined)
    setShowModal(true)
  }

  const onAddNote = () => {
    const newNote: Partial<DailyNoteModel> = {
      date: '',
      id: '',
      title: '',
      text: '',
    }
    setNewNote(newNote)
  }

  const onSaveNewNote = () => {
    setNewNote(undefined)
  }

  const setDateToAndFrom = (date: Date) => {
    handleFetchParamsChange()
    setDateTo(formatDate(date))
    setDateFrom(formatDate(date))
  }

  const onModalHide = (promise?: Promise<any>) => {
    setEditNews(undefined)
    setShowModal(false)
    if (promise) {
      promise.then(() => {
        handleFetchParamsChange()
        fetchNews()
      })
    } else {
      handleFetchParamsChange()
      fetchNews()
    }
  }

  const StationFilter = () => (
    <div className="d-flex flex-nowrap text-nowrap gap-1">
      <Button
        disabled={stationFilter === null}
        className={getSelectButtonClass(stationFilter === null, theme)}
        variant={'outline-secondary'}
        onClick={() => handleStationFilterChange(null)}
      >
        Vše
      </Button>
      {stations.map((station) => (
        <Button
          key={station.id}
          disabled={stationFilter == station.id}
          variant={'outline-secondary'}
          className={getSelectButtonClass(stationFilter == station.id, theme)}
          onClick={() => handleStationFilterChange(station.id)}
        >
          {station.name}
        </Button>
      ))}
    </div>
  )
  const AreaFilter = () => {
    const areaOptions = areas
      .filter((area) => area.stationId == stationFilter)
      .map((area) => ({
        value: area.id,
        label: area.api8bcId,
      }))
    const customStyles = {
      control: () => ({
        width: 145,
        ...(!stationFilter && {
          backgroundColor: theme === 'dark' ? 'black' : 'lightgray',
        }),
      }),
      clearIndicator: () => ({
        padding: '5px 2px 8px 0',
      }),
    }
    return (
      <ReactSelect
        defaultValue={
          areaFilter !== null
            ? areaOptions.find((option) => option.value === areaFilter)
            : null
        }
        placeholder={'Okruh'}
        options={areaOptions}
        isClearable={true}
        isDisabled={!stationFilter || stationFilter === ''}
        styles={reactSelectDarkmode(theme, customStyles)}
        onChange={(option) =>
          handleAreaFilterChange(option ? option.value : null)
        }
      />
    )
  }
  const SectionFilter = () => {
    const sectionOptions = sections.map((section) => ({
      value: section,
      label: section,
    }))

    return (
      <ReactSelect
        defaultValue={
          sectionFilter !== null
            ? sectionOptions.find((option) => option.value === sectionFilter)
            : null
        }
        placeholder={'Sekce'}
        options={sectionOptions}
        isClearable={true}
        isSearchable={true}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) =>
          handleSectionFilterChange(option ? option.value : null)
        }
      />
    )
  }
  const AuthorFilter = () => {
    const authorOptions = authors.map((author) => ({
      value: author.username,
      label: author.username,
    }))

    return (
      <ReactSelect
        defaultValue={
          authorFilter !== null
            ? authorOptions.find((option) => option.value === authorFilter)
            : null
        }
        placeholder={'Author'}
        options={authorOptions}
        isClearable={true}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) =>
          handleAuthorFilterChange(option ? option.value : null)
        }
      />
    )
  }
  const Sorting = () => {
    const sortingOptions = [
      {
        value: SortOrder.DESCENDING,
        label: 'Od nejnovější',
      },
      {
        value: SortOrder.ASCENDING,
        label: 'Od nejstarší',
      },
    ]

    return (
      <ReactSelect
        defaultValue={
          sorting !== null
            ? sortingOptions.find((option) => option.value === sorting)
            : null
        }
        options={sortingOptions}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) =>
          handleSortFilterChange(option ? option.value : SortOrder.DESCENDING)
        }
      />
    )
  }
  const StateSelect = () => {
    const stateOptions = Object.keys(NewsState)
      .filter((key) => Number.isInteger(Number(key)))
      .map((state) => ({
        label: getNewsStateTranslation(state),
        value: Number(state),
      }))

    return (
      <ReactSelect
        defaultValue={
          stateFilter !== null
            ? stateOptions.find((option) => option.value == stateFilter)
            : null
        }
        placeholder={'Stav'}
        options={stateOptions}
        isClearable={true}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) =>
          handleStateFilterChange(option ? option.value : null)
        }
      />
    )
  }

  const TodayButton = () => {
    let isToday = false
    if (dateFrom && dateTo) {
      isToday = areDatesEqual(new Date(), [dateFrom, dateTo])
    }
    return (
      <InputGroup className="d-flex flex-nowrap text-nowrap">
        <Button
          disabled={isToday}
          variant={'outline-secondary'}
          className={getSelectButtonClass(isToday, theme)}
          onClick={() => setDateToAndFrom(new Date())}
        >
          Dnes
        </Button>
      </InputGroup>
    )
  }

  const TomorrowButton = () => {
    const getTomorrowDate = (): Date => {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    }

    let isTomorrow = false
    if (dateFrom && dateTo) {
      isTomorrow = areDatesEqual(getTomorrowDate(), [dateFrom, dateTo])
    }
    return (
      <InputGroup className="d-flex flex-nowrap text-nowrap">
        <Button
          disabled={isTomorrow}
          variant={'outline-secondary'}
          className={getSelectButtonClass(isTomorrow, theme)}
          onClick={() => setDateToAndFrom(getTomorrowDate())}
        >
          Zítra
        </Button>
      </InputGroup>
    )
  }
  const TimeFrom = () => (
    <InputGroup>
      <FormControl
        type="date"
        value={dateFrom || undefined}
        onChange={(e) => handleDateFromChange(e.target.value)}
        max={dateTo || undefined}
      ></FormControl>
    </InputGroup>
  )
  const TimeTo = () => (
    <InputGroup>
      <FormControl
        type="date"
        value={dateTo || undefined}
        onChange={(e) => handleDateToChange(e.target.value)}
        min={dateFrom || undefined}
      ></FormControl>
    </InputGroup>
  )

  const NoteColumn: FC = () => (
    <div className="d-flex flex-column" style={{ minWidth: '300px' }}>
      {notes.map((note) => (
        <NoteCard key={note.id} note={note}></NoteCard>
      ))}
      {newNote !== undefined && (
        <NoteCard key={-1} note={newNote} onSaveNew={onSaveNewNote}></NoteCard>
      )}
      <Button
        variant="outline-success"
        className="fw-bold d-flex align-items-center justify-content-lg-center p-0"
        onClick={onAddNote}
        style={{ width: '100%', height: '30px' }}
      >
        <BsPlusCircle style={{ width: '65%', height: '65%' }}></BsPlusCircle>
      </Button>
    </div>
  )

  return (
    <>
      <AppPageComponent>
        <PageHeaderComponent>
          <div className="pt-4 pb-2 flex-grow-1">
            <Row className="px-0 mb-2 d-flex">
              <Col className="d-flex flex-grow-0">
                <StationFilter></StationFilter>
              </Col>
              <Col className="d-flex gap-1">
                <Col className="flex-grow-0">
                  <AreaFilter></AreaFilter>
                </Col>
                <Col>
                  <SectionFilter></SectionFilter>
                </Col>
                <Col>
                  <AuthorFilter></AuthorFilter>
                </Col>
                <Col>
                  <StateSelect></StateSelect>
                </Col>
                <Col>
                  <Sorting></Sorting>
                </Col>
              </Col>
            </Row>
            <Row className="px-0">
              <Col className="flex-grow-0" style={{minWidth: '164px'}}>
                <Button
                    variant="success"
                    className="fw-bold d-flex align-items-center"
                    onClick={onAddNews}
                >
                  <BsPlusCircleFill className="me-1"></BsPlusCircleFill> Nová zpráva
                </Button>
              </Col>
              <Col className=" me-4">
                <NewsSearch onSearch={handleSearchChange}></NewsSearch>
              </Col>
              <Col className="d-flex px-0">
                <Col className="d-flex flex-grow-0 gap-1 me-2">
                  <TodayButton></TodayButton>
                  <TomorrowButton></TomorrowButton>
                </Col>
                <Col>
                  <TimeFrom></TimeFrom>
                </Col>
                <Col className="ms-2">
                  <TimeTo></TimeTo>
                </Col>
              </Col>
            </Row>
          </div>
        </PageHeaderComponent>
        <PageBodyComponent>
          <div className="d-flex mt-1 mb-5">
            <div className="me-2 flex-grow-1" style={{ height: 'fit-content' }}>
              <Card className="mb-1 bg-light-subtle">
                <Card.Header>
                  <Row className="px-0 fw-bold">
                    <Col>ID</Col>
                    <Col>Autor</Col>
                    <Col>Stav</Col>
                    <Col>Délka</Col>
                    <Col style={{ minWidth: '215px' }}></Col>
                  </Row>
                </Card.Header>
              </Card>
              <ListGroup>
                {newsRefreshed &&
                    <InfiniteScroll
                        dataLength={newss.length}
                        hasMore={!isNewsLoading}
                        next={fetchMoreData}
                        loader={
                          <h4 className="mx-auto mt-5 w-25 text-center text-secondary">
                            Pro načtení další zpráv změntě parametry vyhledávání
                          </h4>
                        }
                        height={850}
                    >
                      {newss.map((news) => (
                          <NewsRow
                              key={news.id}
                              newsId={news.id}
                              onDeleteNews={onDeleteNews}
                              onEditNews={onEditNews}
                          ></NewsRow>
                      ))}
                    </InfiniteScroll>
                }
              </ListGroup>
            </div>
            <NoteColumn></NoteColumn>
          </div>
        </PageBodyComponent>
      </AppPageComponent>
      {showModal && (
        <NewsEditorComponent
          newsToEditId={editNews?.id}
          show={showModal}
          onHide={onModalHide}
        ></NewsEditorComponent>
      )}
    </>
  )
}

const NewsSearch: FC<{ onSearch: (search: string) => void }> = ({
  onSearch,
}) => {
  const [search, setSearch] = useState('')
  const searchInputRef = useRef(null)

  useEffect(() => {
    //@ts-ignore
    searchInputRef.current?.focus()
  }, [search])

  const handleSearchChange = (e: React.ChangeEvent) => {
    e.preventDefault()
    //@ts-ignore
    const searchValue = e.target.value
    setSearch(searchValue)
    onSearch(searchValue)
  }

  return (
    <InputGroup className="">
      <FormControl
        placeholder="Vyhledávaní podle klíčového slova nebo ID"
        ref={searchInputRef}
        onChange={handleSearchChange}
      />
    </InputGroup>
  )
}

export default NewsPage
